import React from 'react';
import { ICONS } from './constants';

interface HelpSidebarProps {
    title: string;
    description: string;
    instructions: string[];
    onRequestHelp?: (preferChat?: boolean) => void;
    onContactConsultant?: () => void;
    theme?: 'light' | 'dark';
}

export const HelpSidebar: React.FC<HelpSidebarProps> = ({ 
    title, 
    description, 
    instructions, 
    onRequestHelp, 
    onContactConsultant,
    theme = 'dark'
}) => {
    const isDark = theme === 'dark';

    const containerClasses = isDark
        ? "w-80 border-l border-slate-700/50 bg-slate-800/20 p-6 flex flex-col shrink-0"
        : "w-80 border-l border-slate-200 bg-slate-50 p-6 flex flex-col shrink-0";

    const titleClasses = isDark ? "font-bold text-slate-200" : "font-bold text-slate-800";
    const descClasses = isDark ? "text-sm text-slate-400 mb-6" : "text-sm text-slate-600 mb-6";
    const highlightClasses = isDark ? "text-blue-400" : "text-blue-600";
    const subtitleClasses = isDark ? "text-xs font-bold text-slate-500 uppercase tracking-widest" : "text-xs font-bold text-slate-400 uppercase tracking-widest";
    const itemClasses = isDark ? "text-sm text-slate-300" : "text-sm text-slate-600";
    const dividerClasses = isDark ? "mt-8 space-y-3 pt-6 border-t border-slate-700/50" : "mt-8 space-y-3 pt-6 border-t border-slate-200";

    const chatButtonClasses = isDark 
        ? "w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold rounded border border-slate-700 hover:border-blue-500/50 transition-all flex items-center justify-center gap-2"
        : "w-full py-2.5 bg-white hover:bg-slate-100 text-slate-700 text-sm font-bold rounded border border-slate-300 hover:border-blue-500/50 transition-all flex items-center justify-center gap-2 shadow-sm";

    const contactButtonClasses = isDark
        ? "w-full py-2.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 text-sm font-bold rounded border border-blue-500/30 transition-all flex items-center justify-center gap-2"
        : "w-full py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-bold rounded border border-blue-200 transition-all flex items-center justify-center gap-2 shadow-sm";

    return (
        <div className={containerClasses}>
            <div className="flex items-center gap-3 mb-4">
                <div className={highlightClasses}>{ICONS.info}</div>
                <h3 className={titleClasses}>{title}</h3>
            </div>
            <p className={descClasses}>{description}</p>
            
            <div className="flex-1 space-y-4 pt-2">
                <h4 className={subtitleClasses}>Instructions</h4>
                <div className="space-y-4">
                    {instructions.map((inst, i) => (
                        <div key={i} className="flex items-start gap-3">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${isDark ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}>
                                {i + 1}
                            </span>
                            <span className={itemClasses}>{inst}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className={dividerClasses}>
                <button 
                    onClick={() => onRequestHelp && onRequestHelp(true)}
                    className={chatButtonClasses}
                >
                    {ICONS.chat} Insights Support Chat
                </button>
                <button 
                    onClick={onContactConsultant}
                    className={contactButtonClasses}
                >
                    {ICONS.users} Contact Consultant
                </button>
            </div>
        </div>
    );
};
